import React, { FC, ChangeEvent } from 'react';
import { Text, Heading, Button, Input, Flex, Select } from 'roo-ui/components';
import styled from '@emotion/styled';
import { FieldLabel, FieldError } from '../../Forms';
import { CALLBACK_TIMES, CALLBACK_DATES } from './constants';

const QFF_OR_EMPTY_REGEX = /(^$|^(([0-9]){10}|([0-9]){1,7})$)/;

const StyledSelect = styled(Select)`
  ::-ms-expand {
    display: none;
  }
`;

interface ErrorShape {
  message?: string;
}

interface DetailsShape {
  fullName?: string;
  phoneNumber?: string;
  qffNumber?: string;
}

interface FormErrors {
  fullName?: ErrorShape;
  phoneNumber?: ErrorShape;
  qffNumber?: ErrorShape;
  callbackTime?: ErrorShape;
  callbackDate?: ErrorShape;
}

export interface RequestCallbackDetailsProps {
  register: Function;
  errors: FormErrors;
  setValue: Function;
  prefilledDetails?: DetailsShape;
}

export const RequestCallbackDetails: FC<RequestCallbackDetailsProps> = ({
  register,
  errors,
  setValue,
  prefilledDetails = {},
}) => {
  const { fullName, phoneNumber, qffNumber } = prefilledDetails;

  const maskPhoneNumber = (value: string) => {
    setValue('phoneNumber', value.replace(/[^0-9+ ]/g, ''));
  };

  return (
    <>
      <Heading.h3 fontSize="xl" mb={6}>
        How we&apos;ll contact you
      </Heading.h3>
      <Heading.h4 fontSize="md">Your details</Heading.h4>
      <Flex
        flexDirection={['column', 'row']}
        flexWrap="wrap"
        borderBottom={1}
        borderColor="greys.alto"
        pb={5}
        mb={8}
      >
        <FieldLabel
          htmlFor="fullName"
          flex="0 0 50%"
          mb={[5, 3]}
          pr={[0, 4]}
          data-testid="full-name-input"
        >
          Name
          <Input
            id="fullName"
            name="fullName"
            defaultValue={fullName}
            ref={register({ required: 'Please enter your name' })}
            error={!!errors.fullName}
          />
          <FieldError error={errors.fullName} data-testid="full-name-error" />
        </FieldLabel>
        <FieldLabel
          htmlFor="phoneNumber"
          flex="0 0 50%"
          mb={[5, 3]}
          pr={[0, 4]}
          data-testid="phone-number-input"
        >
          Phone number
          <Input
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              maskPhoneNumber(e.target.value)
            }
            id="phoneNumber"
            name="phoneNumber"
            defaultValue={phoneNumber}
            ref={register({ required: 'Please enter your phone number' })}
            error={!!errors.phoneNumber}
          />
          <FieldError
            error={errors.phoneNumber}
            data-testid="phone-number-error"
          />
        </FieldLabel>
        <FieldLabel
          htmlFor="qffNumber"
          flex="0 0 50%"
          mb={[5, 3]}
          pr={[0, 4]}
          data-testid="qff-number-input"
        >
          <Flex justifyContent="space-between" alignItems="center">
            Qantas Frequent Flyer number
            <Text color="greys.dusty" fontSize="xs">
              optional
            </Text>
          </Flex>
          <Input
            id="qffNumber"
            name="qffNumber"
            defaultValue={qffNumber}
            ref={register({
              pattern: {
                value: QFF_OR_EMPTY_REGEX,
                message: 'Please enter a valid Frequent Flyer Number',
              },
            })}
            error={!!errors.qffNumber}
          />
          <FieldError error={errors.qffNumber} data-testid="qff-number-error" />
          <Text>So we know how to award your Qantas Points</Text>
        </FieldLabel>
      </Flex>

      <Heading.h4 fontSize="md">When&apos;s the best time to call?</Heading.h4>
      <Flex flexDirection={['column', 'row']} flexWrap="wrap" mb={5}>
        <FieldLabel
          htmlFor="callbackTime"
          flex="0 0 50%"
          mb={[5, 3]}
          pr={[0, 4]}
          data-testid="callback-time-select"
        >
          <Flex justifyContent="space-between" alignItems="center">
            Time
            <Text color="greys.dusty" fontSize="xs">
              optional
            </Text>
          </Flex>
          <StyledSelect
            id="callbackTime"
            name="callbackTime"
            ref={register('callbackTime')}
            error={!!errors.callbackTime}
          >
            <option value="">Select</option>
            {CALLBACK_TIMES.map((timeslot) => (
              <option key={timeslot.title} value={timeslot.value}>
                {timeslot.title}
              </option>
            ))}
          </StyledSelect>
          <FieldError
            error={errors.callbackTime}
            data-testid="callback-time-error"
          />
        </FieldLabel>
        <FieldLabel
          htmlFor="callbackDate"
          flex="0 0 50%"
          mb={[5, 3]}
          pr={[0, 4]}
          data-testid="callback-date-select"
        >
          <Flex justifyContent="space-between" alignItems="center">
            Date
            <Text color="greys.dusty" fontSize="xs">
              optional
            </Text>
          </Flex>
          <StyledSelect
            id="callbackDate"
            name="callbackDate"
            ref={register('callbackDate')}
            error={!!errors.callbackDate}
          >
            <option value="">Select</option>
            {CALLBACK_DATES.map((timeslot) => (
              <option key={timeslot.title} value={timeslot.value}>
                {timeslot.title}
              </option>
            ))}
          </StyledSelect>
          <FieldError
            error={errors.callbackDate}
            data-testid="callback-date-error"
          />
        </FieldLabel>
      </Flex>

      <Button
        type="submit"
        variant="primary"
        width={['100%', '220px']}
        data-testid="submit-callback-button"
        mb={8}
      >
        Submit
      </Button>
      <Text display="block">
        We will only use your information for the purpose of processing this
        enquiry
      </Text>
    </>
  );
};
